'use client';

import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { useCallback } from 'react';
import { MarketingRuntimeConfig, SeedingItem } from '@/app/(activity)/campaigns/[slug]/types';
import { SeedingDetailProduct } from '@/app/(activity)/campaigns/[slug]/types';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { env } from '@/utils/env';
import { useCampaignPageTracking } from '../../../hooks/useCampaignPageTracking';

/** 种草页面埋点公共字段 */
export interface SeedingTrackingCommon {
  page_name: string;
  market_activity_name: string;
  market_activity_id: string;
  page_id: string;
}

/** 获取公共埋点字段 */
export function getTrackingCommon(runtimeConfig: MarketingRuntimeConfig): SeedingTrackingCommon {
  return {
    page_name: runtimeConfig.page_name || '',
    market_activity_name: runtimeConfig.activity_name || '',
    market_activity_id: `${runtimeConfig.activity_id || ''}`,
    page_id: `${runtimeConfig.marketing_page_id || ''}`,
  };
}

export function useSeedingTracking(runtimeConfig: MarketingRuntimeConfig) {
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { trackPageView, trackPageLeave, trackAdClick, trackAdExp } =
    useCampaignPageTracking(runtimeConfig);

  /**
   * 种草坑位点击
   */
  const trackProductClick = useCallback(
    (_item: SeedingItem, index: number) => {
      trackAdClick('商品版位', index, undefined, {
        pits_jump_type: '5',
        pits_jump_content: '种草页面详情弹窗',
      });
    },
    [trackAdClick],
  );

  /**
   * 种草坑位曝光
   */
  const trackProductExposure = useCallback(
    (_item: SeedingItem, index: number) => {
      trackAdExp('商品版位', '', index, {
        pits_jump_type: '5',
        pits_jump_content: '种草页面详情弹窗',
      });
    },
    [trackAdExp],
  );

  /**
   * 种草坑位弹窗曝光
   */
  const trackCampaignPopupExp = useCallback(
    (index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPagePopUp_Exp',
            properties: {
              ...getTrackingCommon(runtimeConfig),
              module_name: '商品版位-弹窗',
              pits_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Seeding campaign popup exposure tracking error:', error);
      }
    },
    [trackEvent, runtimeConfig],
  );
  const getComponentProductData = useCallback(
    (item: SeedingDetailProduct) => {
      const nowPriceInfo = getPriceInfo(userInfo, item, currency);
      const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);
      return {
        business_sku: `${item.id || ''}`,
        shop_sku: item.name || '',
        product_sku: item.shop_sku || '',
        product_id: `${item.id || ''}`,
        product_name: item.name || '',
        product_price: parseFloat(nowPriceInfo.price || '0'),
        current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
        base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
        dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
        currency: currency?.name || '',
        product_class_name: item?.products_category || '',
      };
    },
    [currency, userInfo],
  );
  /**
   * 种草坑位弹窗点击
   */
  const trackCampaignPopupClick = useCallback(
    (item: SeedingDetailProduct, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPagePopUpProduct_Click',
            properties: {
              ...getTrackingCommon(runtimeConfig),
              ...getComponentProductData(item),
              module_name: '商品版位-弹窗',
              pits_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Seeding campaign popup click tracking error:', error);
      }
    },
    [getComponentProductData, runtimeConfig, trackEvent],
  );

  /**
   * 种草坑位弹窗收藏点击
   */
  const trackCampaignPopupCollectClick = useCallback(
    (item: SeedingDetailProduct, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageCollectBtn_Click',
            properties: {
              ...getTrackingCommon(runtimeConfig),
              ...getComponentProductData(item),
              module_name: '商品版位-弹窗',
              pits_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Seeding campaign popup close tracking error:', error);
      }
    },
    [getComponentProductData, runtimeConfig, trackEvent],
  );

  return {
    trackPageView,
    trackPageLeave,
    trackProductClick,
    trackProductExposure,
    trackCampaignPopupExp,
    trackCampaignPopupCollectClick,
    trackCampaignPopupClick,
  };
}
